import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';

const money = (v) => new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(Number(v) || 0);
const formatDate = (s) => s ? new Date(s).toLocaleString('en-ZA') : '—';
const errorText = (e) => e.response?.data?.error || e.message;

// Same banks and universal branch codes as the app's frontEnd/eduthrift/src/utils/banks.ts
const SA_BANKS = [
  { name: 'ABSA', branchCode: '632005' },
  { name: 'Access Bank', branchCode: '410105' },
  { name: 'African Bank', branchCode: '430000' },
  { name: 'Bank Zero', branchCode: '888000' },
  { name: 'Bidvest Bank', branchCode: '462005' },
  { name: 'Capitec Bank', branchCode: '470010' },
  { name: 'Capitec Business', branchCode: '450105' },
  { name: 'Discovery Bank', branchCode: '679000' },
  { name: 'FNB', branchCode: '250655' },
  { name: 'Grindrod Bank', branchCode: '584000' },
  { name: 'Investec', branchCode: '580105' },
  { name: 'Nedbank', branchCode: '198765' },
  { name: 'Postbank', branchCode: '460005' },
  { name: 'RMB Private Bank', branchCode: '250655' },
  { name: 'Sasfin Bank', branchCode: '683000' },
  { name: 'Standard Bank', branchCode: '051001' },
  { name: 'TymeBank', branchCode: '678910' },
];

const ACCOUNT_TYPES = [
  { value: 'cheque', label: 'Cheque / Current' },
  { value: 'savings', label: 'Savings' },
  { value: 'transmission', label: 'Transmission' },
];

const emptyBankForm = { bankName: '', bankAccountNumber: '', bankAccountType: '', bankBranchCode: '' };

const WalletsPage = () => {
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [onlyWithBalance, setOnlyWithBalance] = useState(true);
  const [search, setSearch] = useState('');
  const [statements, setStatements] = useState({});
  const [bankFormFor, setBankFormFor] = useState(null);
  const [bankForm, setBankForm] = useState(emptyBankForm);
  const [busy, setBusy] = useState(null);
  const [messages, setMessages] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/admin/tradesafe/wallets', { timeout: 60000 });
      setWallets(res.data || []);
    } catch (e) {
      setError(errorText(e) || 'Failed to load TradeSafe wallets.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return wallets
      .filter(w => !onlyWithBalance || Number(w.balance) > 0)
      .filter(w => !q || [w.name, w.email, w.userName, w.schoolShopName, w.tokenId]
        .some(v => (v || '').toLowerCase().includes(q)))
      .sort((a, b) => Number(b.balance) - Number(a.balance));
  }, [wallets, onlyWithBalance, search]);

  const totalHeld = useMemo(() => wallets.reduce((sum, w) => sum + (Number(w.balance) || 0), 0), [wallets]);

  const say = (tokenId, ok, text) => setMessages(m => ({ ...m, [tokenId]: { ok, text } }));

  const toggleStatement = async (tokenId) => {
    if (statements[tokenId]) {
      setStatements(s => { const next = { ...s }; delete next[tokenId]; return next; });
      return;
    }
    setStatements(s => ({ ...s, [tokenId]: { loading: true } }));
    try {
      const res = await api.get(`/admin/tradesafe/wallets/${tokenId}/statement`);
      setStatements(s => ({ ...s, [tokenId]: { rows: res.data || [] } }));
    } catch (e) {
      setStatements(s => ({ ...s, [tokenId]: { error: errorText(e) } }));
    }
  };

  const openBankForm = (tokenId) => {
    setBankFormFor(bankFormFor === tokenId ? null : tokenId);
    setBankForm(emptyBankForm);
  };

  const addBankAccount = async (w, body, description) => {
    const ok = window.confirm(`Add ${description} to ${w.name}'s TradeSafe token?\n\n`
      + 'TradeSafe only allows a bank account to be added once — it cannot be changed afterwards.');
    if (!ok) return;
    setBusy(w.tokenId);
    try {
      const res = await api.post(`/admin/tradesafe/wallets/${w.tokenId}/bank-account`, body, { timeout: 60000 });
      setBankFormFor(null);
      say(w.tokenId, true, res.data.immediatePayouts
        ? 'Bank account added. Future payouts and refunds to this token go straight to the bank.'
        : `Bank account added. TradeSafe didn't switch on automatic payouts (${res.data.settingsError}), so use Pay out for future amounts too.`);
      await load();
    } catch (e) {
      say(w.tokenId, false, errorText(e));
    } finally {
      setBusy(null);
    }
  };

  const submitBankForm = (w) => {
    const bank = `${bankForm.bankName} account …${bankForm.bankAccountNumber.slice(-4)}`;
    addBankAccount(w, bankForm, bank);
  };

  const payOut = async (w) => {
    const ok = window.confirm(`Pay out ${money(w.balance)} from ${w.name}'s TradeSafe wallet to ${w.bank} …${w.accountLast4}?\n\n`
      + "TradeSafe pays it in its next payment run and deducts its withdrawal fee.");
    if (!ok) return;
    setBusy(w.tokenId);
    try {
      const res = await api.post(`/admin/tradesafe/wallets/${w.tokenId}/withdraw`, {}, { timeout: 60000 });
      say(w.tokenId, true, `Withdrawal of ${money(res.data.withdrawn)} requested. It appears as a pending debit on the statement until TradeSafe's bank pays it.`);
      await load();
    } catch (e) {
      say(w.tokenId, false, errorText(e));
    } finally {
      setBusy(null);
    }
  };

  const setInstantPayouts = async (w) => {
    const ok = window.confirm(`Switch ${w.name}'s TradeSafe token to immediate payouts?\n\n`
      + `Released funds will then go straight to ${w.bank} …${w.accountLast4} instead of waiting in the wallet.`);
    if (!ok) return;
    setBusy(w.tokenId);
    try {
      await api.post(`/admin/tradesafe/wallets/${w.tokenId}/instant-payouts`, {}, { timeout: 60000 });
      say(w.tokenId, true, 'Immediate payouts switched on. Future releases go straight to the bank account.');
      await load();
    } catch (e) {
      say(w.tokenId, false, errorText(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '16px' }}>
        <div>
          <h2 style={{ margin: 0 }}>TradeSafe Wallets</h2>
          <p style={{ margin: '4px 0 0', color: '#7f8c8d', fontSize: '13px', maxWidth: '760px' }}>
            Payouts and refunds to a TradeSafe token without a bank account wait in that token's wallet.
            Sellers and buyers don't have TradeSafe logins, so pay them out from here: add their bank
            account to the token (once), then Pay out.
          </p>
        </div>
        <button onClick={load} style={{ padding: '8px 16px', cursor: 'pointer' }}>Refresh</button>
      </div>

      <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
        <input
          placeholder="Search name, email or token"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ padding: '8px 10px', width: '280px', border: '1px solid #ccc', borderRadius: '4px' }}
        />
        <label style={{ fontSize: '14px' }}>
          <input type="checkbox" checked={onlyWithBalance} onChange={e => setOnlyWithBalance(e.target.checked)} />
          {' '}Only wallets with money in them
        </label>
        {!loading && <span style={{ fontSize: '14px', color: '#555' }}>Total in wallets: <strong>{money(totalHeld)}</strong></span>}
      </div>

      {error && <p style={{ color: 'red' }}>{error}</p>}

      {loading ? (
        <p>Loading from TradeSafe…</p>
      ) : shown.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#666', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
          <p style={{ fontSize: '18px' }}>{onlyWithBalance ? 'No money waiting in TradeSafe wallets' : 'No tokens match'}</p>
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5' }}>
              <th style={th}>Token</th>
              <th style={th}>Eduthrift</th>
              <th style={th}>Balance</th>
              <th style={th}>Bank account</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {shown.map(w => (
              <React.Fragment key={w.tokenId}>
                <tr style={{ borderBottom: '1px solid #eee' }}>
                  <td style={td}>
                    <strong>{w.name}</strong><br />
                    <span style={muted}>{w.email}</span><br />
                    <span style={muted}>{w.tokenId}{w.reference ? ` · ${w.reference}` : ''}</span>
                  </td>
                  <td style={td}>
                    {w.schoolShopName ? <>School shop: {w.schoolShopName}</>
                      : w.userName ? <>{w.userName} <span style={muted}>(user {w.userId})</span></>
                      : <span style={muted}>No Eduthrift account</span>}
                    {(w.userName || w.schoolShopName) && (
                      <><br /><span style={muted}>{w.currentToken ? 'Current token' : 'Older token'}</span></>
                    )}
                  </td>
                  <td style={{ ...td, fontWeight: 600 }}>{money(w.balance)}</td>
                  <td style={td}>
                    {w.hasBankAccount ? <>{w.bank} …{w.accountLast4}</> : <span style={{ color: '#c0392b' }}>None</span>}
                    {w.hasBankAccount && (
                      <><br /><span style={{ ...muted, color: w.payoutInterval === 'IMMEDIATE' ? '#27ae60' : '#c0392b' }}>
                        Payouts: {w.payoutInterval === 'IMMEDIATE' ? 'immediate' : w.payoutInterval ? w.payoutInterval.toLowerCase() : 'unknown'}
                      </span></>
                    )}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button onClick={() => toggleStatement(w.tokenId)} style={{ ...btn, backgroundColor: '#ecf0f1' }}>
                      {statements[w.tokenId] ? 'Hide statement' : 'Statement'}
                    </button>{' '}
                    {!w.hasBankAccount && (
                      <button onClick={() => openBankForm(w.tokenId)} disabled={!!busy}
                        style={{ ...btn, backgroundColor: '#f39c12', color: '#fff' }}>
                        Add bank account
                      </button>
                    )}
                    {w.hasBankAccount && w.payoutInterval !== 'IMMEDIATE' && (
                      <button onClick={() => setInstantPayouts(w)} disabled={!!busy}
                        style={{ ...btn, backgroundColor: '#27ae60', color: '#fff' }}>
                        {busy === w.tokenId ? 'Saving…' : 'Set instant payouts'}
                      </button>
                    )}{' '}
                    {w.hasBankAccount && Number(w.balance) > 0 && (
                      <button onClick={() => payOut(w)} disabled={!!busy}
                        style={{ ...btn, backgroundColor: '#27ae60', color: '#fff' }}>
                        {busy === w.tokenId ? 'Requesting…' : `Pay out ${money(w.balance)}`}
                      </button>
                    )}
                  </td>
                </tr>

                {messages[w.tokenId] && (
                  <tr><td colSpan={5} style={{ padding: '0 12px 10px' }}>
                    <div style={{
                      backgroundColor: messages[w.tokenId].ok ? '#e8f5e9' : '#fdecea',
                      border: `1px solid ${messages[w.tokenId].ok ? '#27ae60' : '#e74c3c'}`,
                      borderRadius: '6px', padding: '8px 12px', fontSize: '13px'
                    }}>
                      {messages[w.tokenId].ok ? '✅ ' : '❌ '}{messages[w.tokenId].text}
                    </div>
                  </td></tr>
                )}

                {bankFormFor === w.tokenId && (
                  <tr><td colSpan={5} style={{ padding: '0 12px 14px' }}>
                    <div style={{ backgroundColor: '#fafafa', border: '1px solid #ddd', borderRadius: '6px', padding: '12px 14px' }}>
                      {w.userBank && (
                        <div style={{ marginBottom: '12px' }}>
                          <button
                            onClick={() => addBankAccount(w, { fromUserId: w.userId }, `${w.userName}'s bank account (${w.userBank})`)}
                            disabled={!!busy}
                            style={{ ...btn, backgroundColor: '#3498db', color: '#fff' }}
                          >
                            Use {w.userName}'s bank details on Eduthrift: {w.userBank}
                          </button>
                          <span style={{ ...muted, marginLeft: '10px' }}>or type them in:</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <select
                          value={bankForm.bankName}
                          onChange={e => {
                            const bank = SA_BANKS.find(b => b.name === e.target.value);
                            setBankForm(f => ({ ...f, bankName: e.target.value, bankBranchCode: bank ? bank.branchCode : f.bankBranchCode }));
                          }}
                          style={input}
                        >
                          <option value="">Bank…</option>
                          {SA_BANKS.map(b => <option key={b.name} value={b.name}>{b.name}</option>)}
                        </select>
                        <input placeholder="Account number" value={bankForm.bankAccountNumber}
                          onChange={e => setBankForm(f => ({ ...f, bankAccountNumber: e.target.value }))} style={input} />
                        <select value={bankForm.bankAccountType}
                          onChange={e => setBankForm(f => ({ ...f, bankAccountType: e.target.value }))} style={input}>
                          <option value="">Account type…</option>
                          {ACCOUNT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                        <input placeholder="Branch code" value={bankForm.bankBranchCode}
                          onChange={e => setBankForm(f => ({ ...f, bankBranchCode: e.target.value }))} style={{ ...input, width: '110px' }} />
                        <button onClick={() => submitBankForm(w)} disabled={!!busy}
                          style={{ ...btn, backgroundColor: '#f39c12', color: '#fff' }}>
                          {busy === w.tokenId ? 'Saving…' : 'Add bank account'}
                        </button>
                      </div>
                    </div>
                  </td></tr>
                )}

                {statements[w.tokenId] && (
                  <tr><td colSpan={5} style={{ padding: '0 12px 14px' }}>
                    {statements[w.tokenId].loading ? <span style={muted}>Loading statement…</span>
                      : statements[w.tokenId].error ? <span style={{ color: '#c0392b', fontSize: '13px' }}>{statements[w.tokenId].error}</span>
                      : statements[w.tokenId].rows.length === 0 ? <span style={muted}>No entries</span>
                      : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', backgroundColor: '#fafafa' }}>
                          <tbody>
                            {statements[w.tokenId].rows.map((r, i) => (
                              <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                                <td style={td}>{formatDate(r.createdAt)}</td>
                                <td style={td}>{r.type}</td>
                                <td style={{ ...td, color: r.type === 'DEBIT' ? '#c0392b' : '#27ae60' }}>{money(r.amount)}</td>
                                <td style={td}>{r.status === 'PDNG' ? 'Pending' : r.status === 'ACSP' ? 'Done' : r.status}</td>
                                <td style={td}>{r.reference}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const th = { padding: '10px 12px', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid #ddd' };
const td = { padding: '10px 12px', verticalAlign: 'top' };
const muted = { color: '#888', fontSize: '12px' };
const btn = { padding: '6px 12px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' };
const input = { padding: '6px 8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '13px' };

export default WalletsPage;
